
import * as Enumerable from 'linq';
import { PriceBook, PriceListCategory, PriceListCoverageParameters, PriceListEntity, PriceListEntityCoverage } from '../entities/price.book';
import { DateUtility } from './date.utility';

export enum PricebookStatus {
    Valid = 'Valid',
    ExpiresSoon = 'Expires Soon',
    Expired = 'Expired',
    Pending = 'Pending'
}

export enum PricebookGroupName {
    Global = 'Global',
    Customer = 'Customer',
    Technician = 'Technician'
}

export type PriceBookLike = { name?: string, dateStart: Date, dateEnd: Date };

export class PricebookUtility {

    public static getValidPricebooks(pricebooks: PriceBookLike[]): any[] {

        if (!pricebooks?.length)
            return [];

        const today = DateUtility.getDateTime(new Date()).startOf('day');
        const defaultStartDate = new Date(1980, 1, 1, 12, 0, 0);

        const validPriceboos = Enumerable.from(pricebooks)
            .where(x => {

                if (!x?.dateEnd)
                    return true;

                const pricebookEnd = DateUtility.getDateTime(x?.dateEnd).endOf('day');
                const pricebookStart = DateUtility.getDateTime(x?.dateStart ?? defaultStartDate).startOf('day');

                if (pricebookEnd < today)
                    return false;

                if (pricebookStart > today)
                    return false;

                return true;
            })
            .orderBy(x => x.name)
            .toArray();

        return validPriceboos;

    }


    public static getPricebookStatus(pricebook: PriceBook): PricebookStatus {

        if (!pricebook) return PricebookStatus.Expired;

        const today = DateUtility.getDateTime(new Date()).startOf('day');

        const warningDate = today.plus({ day: 60 }).endOf('day');

        const end = pricebook?.dateEnd
            ? DateUtility.getDateTime(pricebook?.dateEnd).endOf('day')
            : null;

        const start = pricebook?.dateStart
            ? DateUtility.getDateTime(pricebook?.dateStart).startOf('day')
            : null;

        switch (true) {

            case end < today: {
                return PricebookStatus.Expired;
            }

            case end <= warningDate: {
                return PricebookStatus.ExpiresSoon;
            }

            case start > today: {
                return PricebookStatus.Pending;
            }

            default:
                return PricebookStatus.Valid;
        }

    }

    public static getStatusColor(status: PricebookStatus) {
        switch (status) {

            case PricebookStatus.Expired: {
                return '#f65177';
            }

            case PricebookStatus.ExpiresSoon: {
                return 'rgb(242, 157, 57)';
            }

            case PricebookStatus.Pending: {
                return 'rgb(255, 254, 85)';
            }

            default:
                return 'rgb(88, 196, 58)';
        }
    }

    public static getGroupingBuyPricebooks(pricebooks: PriceBook[]): PriceBook[] {

        return Enumerable.from(pricebooks)
            .select(p => {

                const groupName = !!p.technician?._id ? PricebookGroupName.Technician : PricebookGroupName.Global;

                return { ...p, groupName: groupName };
            })
            .orderBy(x => {
                if (x['groupName'] === PricebookGroupName.Technician)
                    return 1;

                if (x['groupName'] === PricebookGroupName.Global)
                    return 2;
            })
            .thenBy(o => o.name)
            .toArray();

    }

    public static getGroupingSellPricebooks(pricebooks: PriceBook[]): PriceBook[] {

        const sellPricebooks = Enumerable.from(pricebooks)
            .select(p => {

                const groupName = !!p.customer?._id ? PricebookGroupName.Customer : PricebookGroupName.Global;

                return { ...p, groupName: groupName };
            })
            .orderBy(x => {
                if (x['groupName'] === PricebookGroupName.Customer)
                    return 1;

                if (x['groupName'] === PricebookGroupName.Global)
                    return 2;
            })
            .thenBy(o => o.name)
            .toArray();

        return sellPricebooks;

    }

    public static isRecommended(pricebook: PriceBook | PriceListEntityCoverage, coverageParameters: PriceListCoverageParameters, category: PriceListCategory): boolean {
        //A pricebook will be recommended if and only if
        //The pricebook has a particular coverage parameter, and we match also have supplied that coverage parameter.

        //This is different from the import, as we are not trying to select a pricebook, only float up ones that match

        const sets = [
            {
                has: pricebook?.jobTypes?.length > 0,
                matches: Enumerable.from(pricebook?.jobTypes ?? []).any(j => j._id === coverageParameters?.jobType?._id),
            },
            {
                has: pricebook?.priorities?.length > 0,
                matches: Enumerable.from(pricebook?.priorities ?? []).any(p => p._id === coverageParameters?.jobPriority?._id),
            },
            {
                has: pricebook?.zones?.length > 0,
                matches: Enumerable.from(pricebook?.zones ?? []).any(pZone => Enumerable.from(coverageParameters?.zones ?? []).any(zone => pZone._id === zone._id)),
            },
        ];

        if (category === PriceListCategory.Contractor || category === PriceListCategory.Supplier) {
            const matches = [
                {
                    has: pricebook?.companyCoverages?.length > 0,
                    matches: Enumerable.from(pricebook?.companyCoverages ?? []).any(c => c._id === coverageParameters?.company?._id),
                },
                {
                    has: pricebook?.customerCoverages?.length > 0,
                    matches: Enumerable.from(pricebook?.customerCoverages ?? []).any(c => c._id === coverageParameters?.customer?._id),
                },
                {
                    has: pricebook?.workCategoryCoverages?.length > 0,
                    matches: Enumerable.from(pricebook?.workCategoryCoverages ?? []).any(c => c._id === coverageParameters?.workCategory?._id),
                },
            ]

            sets.push(...matches);
        }

        const matchingSets = sets.filter(s => s.has && s.matches);

        const allSetsMatch = matchingSets.length != 0 && matchingSets.length === sets.filter(s => s.has).length;

        return allSetsMatch;
    }

    public static pickRecommendedPriceBookForAutoSet(pricebooks: PriceListEntity[]): { pricebook: PriceListEntity, message?: string } {

        if (pricebooks.length === 0) {
            return { pricebook: null };
        }

        const available = pricebooks.filter(x => x.group === 'Recommended');

        if (!available?.length) {
            return { pricebook: null };
        }

        // ge the recommended pricebook, because it match the coverage
        if (available?.length === 1) {
            return { pricebook: available[0] };
        }

        return { pricebook: null, message: 'Multiple pricebooks found.' };
    }
}